import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import { useSelector } from 'store';
import NavItem from '../NavItem';
import SkeletonTreeSubList from 'ui-component/skeleton/TreeSubList';

// project hooks
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const menuContainsSelectedItem = (menu, selectedItemId) => {
    if (!menu || !selectedItemId) {
        return false;
    }

    if (menu?.id === selectedItemId) {
        return true;
    }

    return (menu?.children || []).some((child) => menuContainsSelectedItem(child, selectedItemId));
};

const NavCollapse = ({ menu, level }) => {
    const theme = useTheme();

    const { borderRadius } = useConfig();
    const { user } = useAuth();

    const { selectedSideMenuItem } = useSelector((state) => state.main);
    const { isLoading: busOppTypeIsLoading } = useSelector((state) => state.businessOpportunityType);
    const { isLoading: workOrderTypeIsLoading } = useSelector((state) => state.workOrderType);

    const [open, setOpen] = useState(false);

    const isSelectedBranch = useMemo(() => menuContainsSelectedItem(menu, selectedSideMenuItem), [menu, selectedSideMenuItem]);

    useEffect(() => setOpen(isSelectedBranch), [isSelectedBranch]);

    if (!user?.authorizations?.some((auth) => auth?.startsWith(menu?.id))) return <></>;
    if (menu?.id === 'workmgmt-busopp' && busOppTypeIsLoading) return <SkeletonTreeSubList />;
    if (menu?.id === 'workmgmt-workorder' && workOrderTypeIsLoading) return <SkeletonTreeSubList />;

    return (
        <>
            <ListItemButton
                sx={{
                    borderRadius: `${borderRadius}px`,
                    mb: 0.5,
                    alignItems: 'flex-start',
                    backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                    py: level > 1 ? 1 : 1.25,
                    pl: `${level * 24}px`
                }}
                selected={isSelectedBranch}
                onClick={() => setOpen((prev) => !prev)}
            >
                <ListItemIcon sx={{ my: 'auto', minWidth: !menu?.icon ? 18 : 36 }}>
                    {menu?.icon || (
                        <FiberManualRecordIcon
                            sx={{
                                width: isSelectedBranch ? 8 : 6,
                                height: isSelectedBranch ? 8 : 6
                            }}
                            fontSize={level > 0 ? 'inherit' : 'medium'}
                        />
                    )}
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography variant={isSelectedBranch ? 'h5' : 'body1'} color="inherit" sx={{ my: 'auto' }}>
                            {menu?.title}
                        </Typography>
                    }
                    secondary={
                        menu?.caption && (
                            <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block" gutterBottom>
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
                {open ? (
                    <ExpandLessIcon fontSize="small" sx={{ marginTop: 'auto', marginBottom: 'auto' }} />
                ) : (
                    <ExpandMoreIcon fontSize="small" sx={{ marginTop: 'auto', marginBottom: 'auto' }} />
                )}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
                {open && (
                    <List
                        component="div"
                        disablePadding
                        sx={{
                            position: 'relative',
                            '&:after': {
                                content: "''",
                                position: 'absolute',
                                left: '32px',
                                top: 0,
                                height: '100%',
                                width: '1px',
                                opacity: theme.palette.mode === 'dark' ? 0.2 : 1,
                                background: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary.light
                            }
                        }}
                    >
                        {menu?.children?.map((item) => {
                            switch (item?.type) {
                                case 'collapse':
                                    return <NavCollapse key={item?.id} menu={item} level={level + 1} />;
                                case 'item':
                                    return <NavItem key={item?.id} item={item} level={level + 1} />;
                                default:
                                    return (
                                        <Typography key={item?.id} variant="h6" color="error" align="center">
                                            <FormattedMessage id="menuitems-error" />
                                        </Typography>
                                    );
                            }
                        })}
                    </List>
                )}
            </Collapse>
        </>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number
};

export default NavCollapse;
